import React, { useEffect, useState } from "react";
import {
  Card,
  Tabs,
  Descriptions,
  Tag,
  Button,
  Space,
  Spin,
  Badge,
  Alert,
} from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  ArrowLeftOutlined,
  SyncOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import * as tmActions from "store/actions/TenantManagementActions";
import dayjs from "dayjs";

const TENANT_STATUS_COLORS = {
  active: "green",
  pending: "orange",
  setting_up: "blue",
  failed: "red",
  suspended: "volcano",
  cancelled: "default",
};

const SUB_STATUS_COLORS = {
  active: "green",
  inactive: "default",
  pending: "orange",
  expired: "red",
  cancelled: "default",
};

const SaasProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [subsLoading, setSubsLoading] = useState(false);

  const fetchConnection = async () => {
    setLoading(true);
    const data = await tmActions.loadAppConnection(id);
    if (data) setConnection(data);
    setLoading(false);
  };

  const fetchTenants = async () => {
    setTenantsLoading(true);
    const data = await tmActions.loadSyncedTenants({ app_connection: id, page_size: MAX_PAGE_SIZE });
    if (data) setTenants(Array.isArray(data) ? data : data.results || []);
    setTenantsLoading(false);
  };

  const fetchSubscriptions = async () => {
    setSubsLoading(true);
    const data = await tmActions.loadSyncedSubscriptions({ app_connection: id, page_size: MAX_PAGE_SIZE });
    if (data) setSubscriptions(Array.isArray(data) ? data : data.results || []);
    setSubsLoading(false);
  };

  const fetchLogs = async () => {
    const data = await tmActions.loadSyncLogs({ app_connection: id, page_size: MAX_PAGE_SIZE });
    if (data) setSyncLogs(Array.isArray(data) ? data : data.results || []);
  };

  useEffect(() => {
    fetchConnection();
    fetchTenants();
    fetchSubscriptions();
    fetchLogs();
  }, [id]);

  const handleTestConnection = async () => {
    await tmActions.testConnection(id);
    fetchConnection();
  };


  const handleSyncAll = async () => {
    await tmActions.syncAll(id);
    fetchConnection();
    fetchTenants();
    fetchSubscriptions();
    fetchLogs();
  };

  if (loading) {
    return (
      <div style={{ padding: "16px", textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!connection) {
    return (
      <div style={{ padding: "16px" }}>
        <Alert message="App connection not found" type="error" />
      </div>
    );
  }

  const tenantColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <a onClick={() => navigate(`/tenant-management/tenants/${record.id}`)}>{text}</a>
      ),
    },
    { title: "Schema", dataIndex: "schema_name", key: "schema_name", render: (t) => <code>{t}</code> },
    { title: "Domain", dataIndex: "domain", key: "domain" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={TENANT_STATUS_COLORS[s] || "default"}>{s}</Tag>,
    },
    { title: "Admin Email", dataIndex: "admin_email", key: "admin_email" },
    {
      title: "Last Synced",
      dataIndex: "last_synced_at",
      key: "last_synced_at",
      render: (d) => d ? dayjs(d).format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  const subColumns = [
    { title: "Plan", dataIndex: "plan_name", key: "plan_name" },
    {
      title: "Tenant",
      dataIndex: "tenant_name",
      key: "tenant_name",
      render: (t) => t || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={SUB_STATUS_COLORS[s] || "default"}>{s}</Tag>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (a) => `₹${parseFloat(a).toLocaleString()}`,
    },
    { title: "Start", dataIndex: "start_date", key: "start_date" },
    { title: "End", dataIndex: "end_date", key: "end_date" },
  ];

  const logColumns = [
    { title: "Type", dataIndex: "sync_type", key: "sync_type" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => {
        const colors = { started: "processing", success: "success", partial: "warning", failed: "error" };
        return <Tag color={colors[s] || "default"}>{s}</Tag>;
      },
    },
    { title: "Synced", dataIndex: "records_synced", key: "records_synced" },
    { title: "Failed", dataIndex: "records_failed", key: "records_failed" },
    {
      title: "Started",
      dataIndex: "started_at",
      key: "started_at",
      render: (d) => d ? dayjs(d).format("YYYY-MM-DD HH:mm:ss") : "-",
    },
    {
      title: "Completed",
      dataIndex: "completed_at",
      key: "completed_at",
      render: (d) => d ? dayjs(d).format("YYYY-MM-DD HH:mm:ss") : "-",
    },
  ];

  const tabItems = [
    {
      key: "details",
      label: "Details",
      children: (
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Product Name">{connection.product_name}</Descriptions.Item>
          <Descriptions.Item label="Product Code"><code>{connection.product_code}</code></Descriptions.Item>
          <Descriptions.Item label="Backend URL">{connection.backend_url}</Descriptions.Item>
          <Descriptions.Item label="Frontend URL">{connection.frontend_url}</Descriptions.Item>
          <Descriptions.Item label="API Token">{connection.api_token_preview || "Not set"}</Descriptions.Item>
          <Descriptions.Item label="Created">{dayjs(connection.created_at).format("YYYY-MM-DD HH:mm")}</Descriptions.Item>
          <Descriptions.Item label="Updated">{dayjs(connection.updated_at).format("YYYY-MM-DD HH:mm")}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: "connection",
      label: "Connection",
      children: (
        <div>
          <Descriptions bordered column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Status">
              {connection.is_connected ? (
                <Badge status="success" text="Connected" />
              ) : (
                <Badge status="error" text="Disconnected" />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Sync Status">
              <Tag>{connection.sync_status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Last API Call">
              {connection.last_api_call_at
                ? dayjs(connection.last_api_call_at).format("YYYY-MM-DD HH:mm:ss")
                : "Never"}
            </Descriptions.Item>
            <Descriptions.Item label="Last Sync">
              {connection.last_sync_at
                ? dayjs(connection.last_sync_at).format("YYYY-MM-DD HH:mm:ss")
                : "Never"}
            </Descriptions.Item>
          </Descriptions>
          <Space>
            <Button icon={<ApiOutlined />} onClick={handleTestConnection}>
              Test Connection
            </Button>
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={handleSyncAll}
              loading={connection.sync_status === "syncing"}
            >
              Sync All Data
            </Button>
          </Space>
        </div>
      ),
    },
    {
      key: "tenants",
      label: `Tenants (${tenants.length})`,
      children: (
        <Table
          dataSource={tenants}
          columns={tenantColumns}
          rowKey="id"
          loading={tenantsLoading}
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        />
      ),
    },
    {
      key: "subscriptions",
      label: `Subscriptions (${subscriptions.length})`,
      children: (
        <Table
          dataSource={subscriptions}
          columns={subColumns}
          rowKey="id"
          loading={subsLoading}
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        />
      ),
    },
    {
      key: "logs",
      label: "Sync Logs",
      children: (
        <Table
          dataSource={syncLogs}
          columns={logColumns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: "16px" }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/tenant-management/saas-products")}>
          Back
        </Button>
        <span style={{ fontSize: 18, fontWeight: 600 }}>
          {connection.product_name}
          {connection.is_connected ? (
            <Tag icon={<CheckCircleOutlined />} color="success" style={{ marginLeft: 8 }}>Connected</Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="error" style={{ marginLeft: 8 }}>Disconnected</Tag>
          )}
        </span>
      </Space>
      <Card>
        <Tabs items={tabItems} />
      </Card>
    </div>
  );
};

export default SaasProductDetail;
